import React, { useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Helmet } from "react-helmet";
import Grid from "@mui/material/Grid2";
import { motion } from "framer-motion";
import { ThemeProvider } from "@mui/material/styles";
import dineInTheme from "./dineInTheme";
import SpinGame from "../FastFood/SpinGame";
import Match3Game from "../FastFood/Match3Game";
import ThisOrThat from "../Games/ThisOrThat";
import BaseMemoryGame from "../../games/Memory/BaseMemoryGame";
import AboutUs from "../About";
import Footer from "../Footer";

// DineIn-specific content
const GAME_PREVIEWS = [
  {
    key: "wheel",
    title: "Tabletop Spin to Win",
    description:
      "Spin for a chance at appetizers, desserts, or loyalty points!",
    emoji: "🎲",
    color: "#A5B788",
  },
  {
    key: "this-or-that",
    title: "This or That: Menu Edition",
    description: "Fun food choices featuring your signature dishes.",
    emoji: "🍝",
    color: "#FFD97D",
  },
  {
    key: "match-3",
    title: "Menu Match-3",
    description: "Classic match-3 with your entrees and desserts.",
    emoji: "🍰",
    color: "#9B3C3C",
  },
  {
    key: "memory",
    title: "Memory Match",
    description: "Quick, fun memory games with your menu items.",
    emoji: "🍽️",
    color: "#FCEFE3",
  },
];

const DINEIN_PAIRS = [
  { left: "🍝 Fettuccine Alfredo", right: "🍔 Gourmet Burger" },
  { left: "🥗 Caesar Salad", right: "🍕 Margherita Pizza" },
  { left: "🍤 Shrimp Scampi", right: "🍗 Rotisserie Chicken" },
  { left: "🍰 Cheesecake", right: "🍦 Gelato" },
  { left: "🍷 House Red", right: "🍹 Signature Cocktail" },
];

const DINEIN_MEMORY_ITEMS = [
  { id: 1, title: "Lasagna", image: "🍝" },
  { id: 2, title: "Salad", image: "🥗" },
  { id: 3, title: "Pizza", image: "🍕" },
  { id: 4, title: "Chicken", image: "🍗" },
  { id: 5, title: "Shrimp", image: "🍤" },
  { id: 6, title: "Dessert", image: "🍰" },
  { id: 7, title: "Wine", image: "🍷" },
  { id: 8, title: "Gelato", image: "🍦" },
];

function DineInMemoryGame() {
  return (
    <BaseMemoryGame
      title="Dine-In Memory"
      subtitle="Match the menu item to its emoji!"
      shareText="I just played Dine-In Memory on Flying Comet Games!"
      shareUrl={window.location.href}
      successBackgroundColor="#FFD97D"
      shareButtonColor={["#FFD97D", "#5C4B3A"]}
      customItems={DINEIN_MEMORY_ITEMS}
      publicationId="DineInMemoryGame"
      hideTitle={false}
      showDatePicker={false}
    />
  );
}

const FAQS = [
  {
    question: "Do we need to buy hardware?",
    answer:
      "No extra tech here. Our games run right on phones, tablets, or your existing Ziosk-like devices. Simple and seamless.",
    icon: "📱",
    color: "#FFD97D",
  },
  {
    question: "Will this distract from food orders?",
    answer:
      "Not at all. Our games are built to fill the waiting moments — not interrupt meals. Think: a quick, fun distraction while the kitchen does its thing.",
    icon: "⏱",
    color: "#A5B788",
  },
  {
    question: "Is this safe for kids and older guests?",
    answer:
      "Absolutely. Everything is family-friendly, tap-to-play, and easy to enjoy — whether you're 6 or 66.",
    icon: "👨‍👩‍👧",
    color: "#9B3C3C",
  },
  {
    question: "How fast can we launch?",
    answer:
      "You could be live in just 7 days. We'll handle branding, setup, and even the QR codes.",
    icon: "⚡️",
    color: "#FFD97D",
  },
  {
    question: "Can we customize it to our brand or menu?",
    answer:
      "Yes! Every game is branded with your menu items, colors, and style. Guests will feel like it's your experience — not a third-party plug-in.",
    icon: "🎨",
    color: "#A5B788",
  },
];

const DineInHome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <ThemeProvider theme={dineInTheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Helmet>
          <title>
            Custom Games for Dine-In Restaurants – Flying Comet Games
          </title>
          <meta
            name="description"
            content="Delight guests and families with branded games for table-service restaurants. No app required!"
          />
        </Helmet>

        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(rgba(252, 239, 227, 0.97), rgba(252, 239, 227, 0.97)), url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: theme.palette.text.primary,
            pt: { xs: 8, md: 12 },
            pb: { xs: 12, md: 16 },
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "100px",
              background:
                "linear-gradient(to bottom, transparent, rgba(252, 239, 227, 0.9))",
            },
          }}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.6 }}
          >
            <Grid
              container
              spacing={8}
              alignItems="center"
              justifyContent="center"
              sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 } }}
            >
              <Grid item size={{ xs: 12, md: 7 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "2.2rem", md: "4.5rem" },
                    fontWeight: 800,
                    mb: 1,
                    lineHeight: 1.1,
                    color: theme.palette.primary.main,
                    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Turn wait time into play time
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    fontWeight: 400,
                    mb: 4,
                    opacity: 0.9,
                    color: theme.palette.text.primary,
                    maxWidth: "600px",
                  }}
                >
                  Games that entertain your guests and bring them back again —
                  right from their table, no app required.
                </Typography>
                <Button
                  variant="contained"
                  href="https://cal.com/calli-fuchigami/15min"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: "#FFF",
                    px: 6,
                    py: 3,
                    borderRadius: "100px",
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "0 8px 16px rgba(155,60,60,0.2)",
                    minHeight: "2.5em",
                    mb: { xs: 6, md: 0 },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 24px rgba(155,60,60,0.3)",
                    },
                  }}
                >
                  Get a custom game preview →
                </Button>
              </Grid>
              <Grid
                item
                size={{ xs: 12, md: 5 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 340,
                    minHeight: 340,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: -20,
                      left: -20,
                      right: -20,
                      bottom: -20,
                      background:
                        "url('https://www.transparenttextures.com/patterns/wood-pattern.png')",
                      borderRadius: "24px",
                      zIndex: -1,
                      opacity: 0.1,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span>🎮</span> Try it live: Menu Match-3
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "24px",
                      overflow: "hidden",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                      border: "12px solid #2C2C2C",
                      position: "relative",
                      background: "#2C2C2C",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-4px) rotate(-1deg)",
                        boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "30px",
                        background:
                          "linear-gradient(to bottom, rgba(0,0,0,0.2), transparent)",
                        borderRadius: "12px 12px 0 0",
                        zIndex: 1,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.1)",
                        zIndex: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "12px",
                        overflow: "hidden",
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(45deg, rgba(255,217,125,0.1), transparent)",
                          zIndex: 1,
                          pointerEvents: "none",
                        },
                      }}
                    >
                      <Match3Game />
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      color: theme.palette.text.primary,
                      mt: 2,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <span>⚡️</span> Plays in under 30 seconds. No download
                    needed.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </Box>

        {/* Value Prop Section */}
        <Box
          py={12}
          sx={{
            position: "relative",
            background: `linear-gradient(rgba(255, 248, 225, 0.9), rgba(255, 248, 225, 0.9)), url('https://www.transparenttextures.com/patterns/linen.png')`,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(165,183,136,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 2,
                  textAlign: "center",
                  color: theme.palette.primary.main,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80px",
                    height: "4px",
                    background: theme.palette.secondary.main,
                    borderRadius: "2px",
                  },
                }}
              >
                Not just games.{" "}
                <span style={{ fontStyle: "italic" }}>Guest magnets.</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: 500,
                  mb: 8,
                  textAlign: "center",
                  color: theme.palette.text.primary,
                  opacity: 0.85,
                }}
              >
                Games that make wait time fly, keep families smiling, and bring
                guests back for more.
              </Typography>

              <Grid
                container
                spacing={6}
                sx={{ mb: 4 }}
                justifyContent="center"
              >
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(155,60,60,0.1)",
                        borderColor: theme.palette.primary.main,
                      },
                      "&::before": {
                        content: '"1"',
                        position: "absolute",
                        top: -20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "40px",
                        height: "40px",
                        background: theme.palette.primary.main,
                        color: "#FFF",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        boxShadow: "0 4px 8px rgba(155,60,60,0.2)",
                      },
                    }}
                  >
                    <Box mb={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <span style={{ fontSize: "2rem" }}>😊</span> Delight
                        Every Table
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.7,
                          color: theme.palette.text.primary,
                        }}
                      >
                        Entertain families, couples, and groups while they
                        wait—no more restless guests.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        width: "100%",
                        background: theme.palette.secondary.main,
                        borderRadius: "0 0 24px 24px",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(255,217,125,0.1)",
                        borderColor: theme.palette.secondary.main,
                      },
                      "&::before": {
                        content: '"2"',
                        position: "absolute",
                        top: -20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "40px",
                        height: "40px",
                        background: theme.palette.secondary.main,
                        color: theme.palette.primary.main,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        boxShadow: "0 4px 8px rgba(255,217,125,0.3)",
                      },
                    }}
                  >
                    <Box mb={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <span style={{ fontSize: "2rem" }}>📱</span> Seamless at
                        Every Seat
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.7,
                          color: theme.palette.text.primary,
                        }}
                      >
                        Guests play on their phones or your tablets—no app, no
                        staff training, just scan and play.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        width: "100%",
                        background: theme.palette.accent.main,
                        borderRadius: "0 0 24px 24px",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(165,183,136,0.1)",
                        borderColor: theme.palette.accent.main,
                      },
                      "&::before": {
                        content: '"3"',
                        position: "absolute",
                        top: -20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "40px",
                        height: "40px",
                        background: theme.palette.accent.main,
                        color: "#FFF",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        boxShadow: "0 4px 8px rgba(165,183,136,0.2)",
                      },
                    }}
                  >
                    <Box mb={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.35rem",
                          fontWeight: 700,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: theme.palette.accent.main,
                        }}
                      >
                        <span style={{ fontSize: "2rem" }}>⚡️</span> Ready in 7
                        Days
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.15rem",
                          lineHeight: 1.7,
                          color: theme.palette.text.primary,
                        }}
                      >
                        Your first custom game, themed for your menu and brand,
                        live in a week.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 8,
                        width: "100%",
                        background: theme.palette.primary.main,
                        borderRadius: "0 0 24px 24px",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* How it Works Section */}
        <Box
          py={12}
          sx={{
            background: `linear-gradient(rgba(252, 239, 227, 0.97), rgba(252, 239, 227, 0.97)), url('https://www.transparenttextures.com/patterns/linen.png')`,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(165,183,136,0.2), transparent)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "url('https://www.transparenttextures.com/patterns/restaurant-pattern.png')",
              opacity: 0.05,
              pointerEvents: "none",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 2,
                  textAlign: "center",
                  color: theme.palette.primary.main,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80px",
                    height: "4px",
                    background: theme.palette.secondary.main,
                    borderRadius: "2px",
                  },
                }}
              >
                How it Works
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 8,
                  color: theme.palette.text.primary,
                  lineHeight: 1.6,
                  textAlign: "center",
                  fontWeight: 400,
                  opacity: 0.9,
                }}
              >
                Takes less than 7 days to launch. No app. No training. Just scan
                and play.
              </Typography>

              <Grid container spacing={6} justifyContent="center">
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(155,60,60,0.1)",
                        borderColor: theme.palette.primary.main,
                      },
                      "&::before": {
                        content: '"1"',
                        position: "absolute",
                        top: -20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "40px",
                        height: "40px",
                        background: theme.palette.primary.main,
                        color: "#FFF",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        boxShadow: "0 4px 8px rgba(155,60,60,0.2)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        background: `${theme.palette.primary.main}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                          background: `${theme.palette.primary.main}25`,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: "3.5rem" }}>🎮</Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        mb: 2,
                        color: theme.palette.primary.main,
                      }}
                    >
                      Pick a game
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Choose from trivia, memory, or menu-themed fun.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(255,217,125,0.1)",
                        borderColor: theme.palette.secondary.main,
                      },
                      "&::before": {
                        content: '"2"',
                        position: "absolute",
                        top: -20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "40px",
                        height: "40px",
                        background: theme.palette.secondary.main,
                        color: theme.palette.primary.main,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        boxShadow: "0 4px 8px rgba(255,217,125,0.3)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        background: `${theme.palette.secondary.main}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                          background: `${theme.palette.secondary.main}25`,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: "3.5rem" }}>🎨</Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        mb: 2,
                        color: theme.palette.primary.main,
                      }}
                    >
                      We brand it for you
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Your food, your colors, your vibe—no staff training
                      needed.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      position: "relative",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(165,183,136,0.1)",
                        borderColor: theme.palette.accent.main,
                      },
                      "&::before": {
                        content: '"3"',
                        position: "absolute",
                        top: -20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "40px",
                        height: "40px",
                        background: theme.palette.accent.main,
                        color: "#FFF",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        boxShadow: "0 4px 8px rgba(165,183,136,0.2)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        background: `${theme.palette.accent.main}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                          background: `${theme.palette.accent.main}25`,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: "3.5rem" }}>🎯</Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        mb: 2,
                        color: theme.palette.accent.main,
                      }}
                    >
                      Guests scan and play
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.15rem",
                        lineHeight: 1.7,
                        color: theme.palette.text.primary,
                      }}
                    >
                      No downloads, no hardware—just scan and play at the table.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* Games Preview Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: theme.palette.background.accent,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(165,183,136,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Box mb={8} sx={{ textAlign: "center", position: "relative" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "2.5rem", md: "4rem" },
                    fontWeight: 800,
                    mb: 3,
                    color: theme.palette.primary.main,
                    position: "relative",
                    display: "inline-block",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "80px",
                      height: "4px",
                      background: theme.palette.secondary.main,
                      borderRadius: "2px",
                    },
                  }}
                >
                  Games Your Guests Will Crave
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.3rem", md: "1.5rem" },
                    maxWidth: "800px",
                    mx: "auto",
                    mt: 4,
                    color: theme.palette.text.primary,
                    lineHeight: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    "&::before, &::after": {
                      content: '""',
                      width: "40px",
                      height: "1px",
                      background: theme.palette.secondary.main,
                      opacity: 0.5,
                    },
                  }}
                >
                  Each one themed around your menu, your vibe — all playable
                  from the table
                </Typography>
              </Box>

              <Grid
                container
                spacing={4}
                sx={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(circle, rgba(165,183,136,0.1) 0%, transparent 70%)",
                    zIndex: 0,
                  },
                }}
              >
                {/* Featured Game - Menu Match-3 */}
                <Grid item size={{ xs: 12, md: 8 }}>
                  <Box
                    sx={{
                      position: "relative",
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        "& .game-badge": {
                          transform: "scale(1.1)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="game-badge"
                      sx={{
                        position: "absolute",
                        top: -12,
                        right: 24,
                        background: theme.palette.secondary.main,
                        color: theme.palette.primary.main,
                        px: 2,
                        py: 0.5,
                        borderRadius: "20px",
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        zIndex: 2,
                        transition: "transform 0.3s ease-in-out",
                        boxShadow: "0 4px 12px rgba(255,217,125,0.3)",
                      }}
                    >
                      Fan Favorite
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "24px",
                        p: 4,
                        height: "100%",
                        border: `2px solid ${theme.palette.secondary.main}`,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(45deg, rgba(255,217,125,0.1), transparent)",
                          zIndex: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: 4,
                          height: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontSize: "2rem",
                              fontWeight: 800,
                              color: theme.palette.primary.main,
                              mb: 2,
                            }}
                          >
                            Menu Match-3
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "1.2rem",
                              color: theme.palette.text.primary,
                              mb: 3,
                              lineHeight: 1.6,
                            }}
                          >
                            Match your favorite dishes in this addictive puzzle
                            game. Perfect for waiting for appetizers!
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: "20px",
                                background: "rgba(165,183,136,0.1)",
                                color: theme.palette.accent.main,
                                fontSize: "0.9rem",
                                fontWeight: 600,
                              }}
                            >
                              Family Friendly
                            </Box>
                            <Box
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: "20px",
                                background: "rgba(255,217,125,0.1)",
                                color: theme.palette.secondary.main,
                                fontSize: "0.9rem",
                                fontWeight: 600,
                              }}
                            >
                              Quick Play
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: 360,
                              position: "relative",
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                top: -20,
                                left: -20,
                                right: -20,
                                bottom: -20,
                                background:
                                  'url("https://www.transparenttextures.com/patterns/wood-pattern.png")',
                                borderRadius: "24px",
                                zIndex: -1,
                                opacity: 0.1,
                              },
                            }}
                          >
                            <Match3Game />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Spin Game */}
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "24px",
                        p: 4,
                        height: "100%",
                        border: "1px solid rgba(155,60,60,0.2)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(45deg, rgba(155,60,60,0.05), transparent)",
                          zIndex: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: 2,
                          }}
                        >
                          Spin to Win
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            color: theme.palette.text.primary,
                            mb: 3,
                            lineHeight: 1.6,
                          }}
                        >
                          Spin for a chance at appetizers, desserts, or loyalty
                          points!
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: 280,
                              position: "relative",
                            }}
                          >
                            <SpinGame size={225} />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mt: 3,
                          }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 0.5,
                              borderRadius: "20px",
                              background: "rgba(155,60,60,0.1)",
                              color: theme.palette.primary.main,
                              fontSize: "0.9rem",
                              fontWeight: 600,
                            }}
                          >
                            Rewards
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* This or That */}
                <Grid item size={{ xs: 12, md: 5 }}>
                  <Box
                    sx={{
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "24px",
                        p: 4,
                        height: "100%",
                        border: "1px solid rgba(255,217,125,0.2)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(45deg, rgba(255,217,125,0.05), transparent)",
                          zIndex: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: 2,
                          }}
                        >
                          This or That: Menu Edition
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            color: theme.palette.text.primary,
                            mb: 3,
                            lineHeight: 1.6,
                          }}
                        >
                          Fun food choices featuring your signature dishes.
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              position: "relative",
                            }}
                          >
                            <ThisOrThat
                              pairs={DINEIN_PAIRS}
                              brandColors={{
                                primary: theme.palette.secondary.main,
                                secondary: theme.palette.primary.main,
                                background: theme.palette.background.paper,
                              }}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mt: 3,
                          }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 0.5,
                              borderRadius: "20px",
                              background: "rgba(255,217,125,0.1)",
                              color: theme.palette.primary.main,
                              fontSize: "0.9rem",
                              fontWeight: 600,
                            }}
                          >
                            Social
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Memory Game */}
                <Grid item size={{ xs: 12, md: 7 }}>
                  <Box
                    sx={{
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "24px",
                        p: 4,
                        height: "100%",
                        border: "1px solid rgba(165,183,136,0.2)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background:
                            "linear-gradient(45deg, rgba(165,183,136,0.05), transparent)",
                          zIndex: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 700,
                            color: theme.palette.accent.main,
                            mb: 2,
                          }}
                        >
                          Memory Match
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            color: theme.palette.text.primary,
                            mb: 3,
                            lineHeight: 1.6,
                          }}
                        >
                          Quick, fun memory games with your menu items.
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              position: "relative",
                            }}
                          >
                            <DineInMemoryGame />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            mt: 3,
                          }}
                        >
                          <Box
                            sx={{
                              px: 2,
                              py: 0.5,
                              borderRadius: "20px",
                              background: "rgba(165,183,136,0.1)",
                              color: theme.palette.accent.main,
                              fontSize: "0.9rem",
                              fontWeight: 600,
                            }}
                          >
                            Family Friendly
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  textAlign: "center",
                  mt: 8,
                }}
              >
                <Button
                  variant="contained"
                  href="https://cal.com/calli-fuchigami/15min"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.primary.main,
                    px: { xs: 6, md: 8 },
                    py: { xs: 2, md: 3 },
                    borderRadius: "100px",
                    fontSize: { xs: "1.2rem", md: "1.4rem" },
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    minHeight: "3em",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#FFF",
                      color: theme.palette.secondary.main,
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  Book a demo
                </Button>
              </Box>
            </motion.div>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box
          py={12}
          sx={{
            background: `linear-gradient(rgba(252, 239, 227, 0.97), rgba(252, 239, 227, 0.97)), url('https://www.transparenttextures.com/patterns/linen.png')`,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(165,183,136,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  fontWeight: 800,
                  mb: 3,
                  textAlign: "center",
                  color: theme.palette.primary.main,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80px",
                    height: "4px",
                    background: theme.palette.secondary.main,
                    borderRadius: "2px",
                  },
                }}
              >
                Questions We Hear Often
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 8,
                  color: theme.palette.text.primary,
                  lineHeight: 1.6,
                  textAlign: "center",
                  fontWeight: 400,
                  opacity: 0.9,
                }}
              >
                Here's what other restaurant partners ask us. We're here to make
                your decision easy.
              </Typography>

              <Box sx={{ maxWidth: "800px", mx: "auto" }}>
                {FAQS.map((faq, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "24px",
                        p: 4,
                        mb: 3,
                        border: "1px solid rgba(0,0,0,0.1)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                          borderColor: faq.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "16px",
                            background: `${faq.color}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "2rem",
                              lineHeight: 1,
                            }}
                          >
                            {faq.icon}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "1.35rem",
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              mb: 1,
                            }}
                          >
                            {faq.question}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "1.15rem",
                              lineHeight: 1.7,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {faq.answer}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Box>
        </Box>

        <Box
          border="1px solid lightgray"
          backgroundColor={theme.palette.background.accent}
        >
          <AboutUs isComponent={true} />
        </Box>

        {/* Final CTA Section */}
        <Box
          py={16}
          sx={{
            background: `linear-gradient(rgba(155, 60, 60, 0.97), rgba(155, 60, 60, 0.97)), url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "#FFF",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "100px",
              background:
                "linear-gradient(to bottom, rgba(252, 239, 227, 0.9), transparent)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at center, rgba(255, 217, 125, 0.15) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box
            maxWidth="1200px"
            mx="auto"
            px={4}
            textAlign="center"
            position="relative"
          >
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", md: "4.5rem" },
                  fontWeight: 800,
                  mb: 4,
                  color: "#FFF",
                  textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "120px",
                    height: "4px",
                    background: theme.palette.secondary.main,
                    borderRadius: "2px",
                  },
                }}
              >
                Let's bring joy to your tables — starting this week
              </Typography>
              <Button
                variant="contained"
                href="https://cal.com/calli-fuchigami/15min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                  px: { xs: 6, md: 8 },
                  py: { xs: 2, md: 3 },
                  borderRadius: "100px",
                  fontSize: { xs: "1.2rem", md: "1.4rem" },
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  minHeight: "3em",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#FFF",
                    color: theme.palette.secondary.main,
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.3)",
                  },
                }}
              >
                Book a demo
              </Button>
            </motion.div>
          </Box>
        </Box>

        <Footer bgColor="white" />
      </Box>
    </ThemeProvider>
  );
};

export default DineInHome;
